import { useState, useRef, useEffect, useCallback } from "react";
import { ForgeLogo } from "./Primitives";
import storage from "../utils/storage";
import { auth, chat, getChatWebSocketUrl } from "../api/client";

const TOOL_LABELS = {
  save_memory: "Memory Saved",
  log_weight: "Weight Logged",
  log_meal: "Meal Logged",
  log_recovery: "Recovery Logged",
  log_body_measurements: "Measurements Logged",
  create_workout: "Workout Created",
  create_workout_program: "Program Created",
  activate_program: "Program Activated",
  generate_program_pdf: "PDF Generated",
  mark_workout_complete: "Workout Completed",
  get_client_stats: "Checked Stats",
  get_workout_history: "Checked Workouts",
  search_knowledge: "Searched KB",
  recall_memories: "Recalled Memories",
  save_progress_photo: "Photo Saved",
  analyze_food_photo: "Photo Analyzed",
  escalate_to_trainer: "Escalated to Trainer",
  emergency_alert: "Emergency Alert Sent",
  present_ped_waiver: "PED Waiver Presented",
  update_client_goals: "Goals Updated",
  mark_onboarding_complete: "Onboarding Complete",
};

// Derive WO base path for photo URLs
function getBasePath() {
  const path = window.location.pathname;
  const match = path.match(/^(.*\/WebObjects\/forge\.woa)/);
  return match ? match[1] : "";
}

// Photo comparison card rendered in chat
function PhotoComparisonCard({ data, C }) {
  const basePath = getBasePath();
  const b = data.before || {};
  const a = data.after || {};
  const bStats = b.stats || {};
  const aStats = a.stats || {};

  const ratingColors = {
    significant_positive: C.secondary || "#4ade80",
    moderate: C.accent,
    minimal: C.text4,
    concerning: C.warn || "#ff6b6b",
  };
  const ratingColor = ratingColors[data.changeRating] || C.accent;

  const statRow = (label, before, after) => {
    if (!before && !after) return null;
    return (
      <div key={label} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${C.structBorder}` }}>
        <span style={{ color: C.text4, fontSize: "var(--fs-xs)", flex: 1 }}>{before || "—"}</span>
        <span style={{ color: C.text3, fontSize: "var(--fs-xs)", flex: 1, textAlign: "center", fontFamily: "var(--m)", letterSpacing: ".06em" }}>{label}</span>
        <span style={{ color: C.text4, fontSize: "var(--fs-xs)", flex: 1, textAlign: "right" }}>{after || "—"}</span>
      </div>
    );
  };

  return (
    <div style={{
      background: C.cardGradient,
      border: `1px solid ${C.accent020}`,
      borderRadius: 16,
      overflow: "hidden",
      boxShadow: `0 0 24px ${C.accent008}, ${C.cardShadow}`,
    }}>
      {/* Header */}
      <div style={{ padding: "12px 16px 8px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: "var(--fs-sm)", fontWeight: 700, color: C.text1, fontFamily: "var(--d)", letterSpacing: ".04em" }}>
          PROGRESS COMPARISON
        </div>
        {data.elapsed && (
          <div style={{ fontSize: "var(--fs-2xs)", color: C.accent, fontFamily: "var(--m)", letterSpacing: ".08em" }}>
            {data.elapsed.toUpperCase()}
          </div>
        )}
      </div>

      {/* Side-by-side photos */}
      <div style={{ display: "flex", gap: 2, padding: "0 8px" }}>
        <div style={{ flex: 1, position: "relative" }}>
          <img
            src={`${basePath}${b.url}`}
            alt="Before"
            style={{ width: "100%", aspectRatio: "3/4", objectFit: "cover", borderRadius: 8, display: "block" }}
          />
          <div style={{
            position: "absolute", bottom: 6, left: 6, right: 6,
            background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
            borderRadius: 6, padding: "4px 8px", textAlign: "center",
          }}>
            <div style={{ fontSize: "var(--fs-2xs)", color: C.text3, fontFamily: "var(--m)", letterSpacing: ".06em" }}>BEFORE</div>
            <div style={{ fontSize: "var(--fs-xs)", color: C.text1 }}>{b.dateDisplay}</div>
          </div>
        </div>
        <div style={{ flex: 1, position: "relative" }}>
          <img
            src={`${basePath}${a.url}`}
            alt="After"
            style={{ width: "100%", aspectRatio: "3/4", objectFit: "cover", borderRadius: 8, display: "block" }}
          />
          <div style={{
            position: "absolute", bottom: 6, left: 6, right: 6,
            background: "rgba(0,0,0,0.7)", backdropFilter: "blur(8px)",
            borderRadius: 6, padding: "4px 8px", textAlign: "center",
          }}>
            <div style={{ fontSize: "var(--fs-2xs)", color: C.accent, fontFamily: "var(--m)", letterSpacing: ".06em" }}>AFTER</div>
            <div style={{ fontSize: "var(--fs-xs)", color: C.text1 }}>{a.dateDisplay}</div>
          </div>
        </div>
      </div>

      {/* Stats overlay */}
      {(Object.keys(bStats).length > 0 || Object.keys(aStats).length > 0) && (
        <div style={{ padding: "10px 16px 6px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: "var(--fs-2xs)", color: C.text5, fontFamily: "var(--m)" }}>BEFORE</span>
            <span style={{ fontSize: "var(--fs-2xs)", color: C.text5, fontFamily: "var(--m)" }}>AFTER</span>
          </div>
          {statRow("WEIGHT", bStats.weight, aStats.weight)}
          {statRow("BODY FAT", bStats.bodyFat ? `${bStats.bodyFat}%` : null, aStats.bodyFat ? `${aStats.bodyFat}%` : null)}
          {statRow("WAIST", bStats.waist, aStats.waist)}
          {statRow("CHEST", bStats.chest, aStats.chest)}
          {statRow("ARMS", bStats.arms, aStats.arms)}
        </div>
      )}

      {/* Rating badge */}
      {data.changeRatingLabel && (
        <div style={{ padding: "6px 16px 12px", textAlign: "center" }}>
          <span style={{
            display: "inline-block", padding: "4px 12px", borderRadius: 20,
            background: `${ratingColor}15`, border: `1px solid ${ratingColor}30`,
            color: ratingColor, fontSize: "var(--fs-xs)", fontWeight: 600,
            fontFamily: "var(--m)", letterSpacing: ".04em",
          }}>
            {data.changeRatingLabel}
          </span>
        </div>
      )}
    </div>
  );
}

// Simple markdown renderer for bold and italic
function renderMarkdown(text) {
  if (!text) return text;
  const parts = [];
  let key = 0;
  // Split on **bold** and *italic* patterns
  const regex = /(\*\*(.+?)\*\*|\*(.+?)\*)/g;
  let lastIndex = 0;
  let match;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      parts.push(<strong key={key++} style={{ fontWeight: 700 }}>{match[2]}</strong>);
    } else if (match[3]) {
      parts.push(<em key={key++}>{match[3]}</em>);
    }
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : text;
}

// Floating coach button for use in WorkoutPlayer and other views
export function CoachFAB({ C, onClick }) {
  return (
    <button onClick={onClick} style={{
      position: "fixed", bottom: 100, right: 20,
      width: 48, height: 48, borderRadius: 14,
      background: C.structGlass,
      border: `1.5px solid ${C.accent030}`,
      backdropFilter: "blur(16px)", WebkitBackdropFilter: "blur(16px)",
      display: "flex", alignItems: "center", justifyContent: "center",
      cursor: "pointer", zIndex: 55,
      boxShadow: `0 0 20px ${C.accent015}, 0 4px 16px rgba(0,0,0,0.3), 0 0 40px ${C.accent005}`,
      animation: "accentBreathe 5s ease-in-out infinite",
      transition: "all 0.2s",
    }}>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l2.1 7.9L22 12l-7.9 2.1L12 22l-2.1-7.9L2 12l7.9-2.1Z" />
      </svg>
    </button>
  );
}

// ══════════════════════════════════════════════════════════════
// FORGE COACH — FULL TAB VIEW
// Real AI coaching via WebSocket connection
// ══════════════════════════════════════════════════════════════

export default function CoachPanel({ C, isOverlay, onClose, isWorkout, visible }) {
  const defaultMsg = { role: "assistant", text: "Hey! I'm your Forge coach. Ask me anything about your training, nutrition, or recovery.", time: Date.now() };
  const [messages, setMessages] = useState(() => {
    const saved = storage.get("coach_messages", null);
    return saved && saved.length > 0 ? saved : [defaultMsg];
  });
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [showPrompts, setShowPrompts] = useState(true);
  const [pendingFiles, setPendingFiles] = useState([]); // [{ preview, name, type, dataUrl }]
  const [listening, setListening] = useState(false);
  const [speakingId, setSpeakingId] = useState(null);
  const [connected, setConnected] = useState(false);
  const [streamingText, setStreamingText] = useState("");
  const [thinkingText, setThinkingText] = useState("");
  const [historyAnimCount, setHistoryAnimCount] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const scrollRef = useRef();
  const photoRef = useRef();
  const galleryRef = useRef();
  const recognitionRef = useRef(null);
  const wsRef = useRef(null);
  const reconnectRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectAttemptRef = useRef(0);
  const streamingRef = useRef("");
  const thinkingRef = useRef("");
  const streamingToolsRef = useRef([]);
  const historyLoadedRef = useRef(false);

  const connectingRef = useRef(false);
  const snapHandoffProcessedRef = useRef(false);

  // Check for snap meal handoff from TodayView
  useEffect(() => {
    if (!visible || snapHandoffProcessedRef.current) return;
    const raw = sessionStorage.getItem("forge_snap_handoff");
    if (!raw) return;
    snapHandoffProcessedRef.current = true;
    try {
      const handoff = JSON.parse(raw);
      if (handoff.message) {
        // Wait for WebSocket connection, then send (max 10s timeout)
        let attempts = 0;
        const maxAttempts = 20;
        const trySend = () => {
          if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
            // Only clear sessionStorage AFTER successful send
            sessionStorage.removeItem("forge_snap_handoff");
            setShowPrompts(false);
            const photo = handoff.photo;
            const photoDataUrl = photo;
            const mimeMatch = photo?.match(/^data:(image\/[a-z]+);base64,/);
            setMessages((prev) => [...prev, { role: "user", text: handoff.message, time: Date.now(), photo: photoDataUrl }]);
            setTyping(true);
            streamingRef.current = "";
            thinkingRef.current = "";
            setStreamingText("");
            setThinkingText("");
            const msg = { type: "message", content: handoff.message };
            if (photo && mimeMatch) {
              msg.file = { data: photo.split(",")[1], mimeType: mimeMatch[1], fileName: "snap_meal.jpg" };
            }
            wsRef.current.send(JSON.stringify(msg));
          } else if (++attempts < maxAttempts) {
            setTimeout(trySend, 500);
          } else {
            // Timed out waiting for WebSocket — leave in sessionStorage for next attempt
            snapHandoffProcessedRef.current = false;
            console.warn("[Forge] snap handoff timed out waiting for WebSocket");
          }
        };
        setTimeout(trySend, 300);
      }
    } catch (e) {
      console.error("[Forge] snap handoff failed", e);
      sessionStorage.removeItem("forge_snap_handoff");
    }
  }, [visible]);

  // Load chat history via REST on mount (reliable, survives tab switches)
  useEffect(() => {
    chat.getHistory().then((data) => {
      if (data?.messages?.length > 0) {
        const loaded = data.messages.map((m) => ({
          role: m.role === "trainer" ? "assistant" : m.role,
          text: m.content,
          time: Date.now(),
          ...(m.trainerName && { trainerName: m.trainerName }),
        }));
        setMessages(loaded);
        setHistoryAnimCount(loaded.length);
        setTimeout(() => setHistoryAnimCount(0), loaded.length * 40 + 500);
        historyLoadedRef.current = true;
      }
    }).catch(() => {}).finally(() => setHistoryLoading(false));
  }, []);

  // WebSocket connection with exponential backoff reconnection
  const connectWebSocket = useCallback(async () => {
    // Guard against duplicate connection attempts
    if (connectingRef.current || (wsRef.current && wsRef.current.readyState === WebSocket.OPEN)) return;
    connectingRef.current = true;
    try {
      // Always get fresh token on reconnect (handles token expiry)
      const tokenData = await auth.getChatToken();
      if (!tokenData?.chatToken) { connectingRef.current = false; return; }

      const url = getChatWebSocketUrl(tokenData.chatToken);
      const ws = new WebSocket(url);
      // Set ref immediately to prevent concurrent connect attempts
      wsRef.current = ws;

      ws.onopen = () => {
        connectingRef.current = false;
        setConnected(true);
        wsRef.current = ws;
        reconnectAttemptsRef.current = 0;
        reconnectAttemptRef.current = 0; // Reset backoff on success
        // Heartbeat ping every 30s to keep connection alive on iOS
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        pingIntervalRef.current = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: "ping" }));
          }
        }, 30000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === "history" && Array.isArray(data.messages)) {
            // Skip if REST already loaded history (avoids flicker/race)
            if (!historyLoadedRef.current) {
              const loaded = data.messages.map((m) => ({
                role: m.role === "trainer" ? "assistant" : m.role,
                text: m.content,
                time: Date.now(),
                ...(m.trainerName && { trainerName: m.trainerName }),
              }));
              if (loaded.length > 0) {
                setMessages(loaded);
                setHistoryAnimCount(loaded.length);
                setTimeout(() => setHistoryAnimCount(0), loaded.length * 40 + 500);
              }
            }
          } else if (data.type === "stream") {
            // Backend now sends deltas (data.delta). Keep data.content fallback
            // for backward compat during rollout.
            const chunk = data.delta || data.content || "";
            if (chunk) {
              streamingRef.current += chunk;
              setStreamingText(streamingRef.current);
            }
            // First visible text — clear any thinking indicator
            if (thinkingRef.current) {
              thinkingRef.current = "";
              setThinkingText("");
            }
          } else if (data.type === "thinking_stream") {
            thinkingRef.current += data.delta || "";
            setThinkingText(thinkingRef.current);
          } else if (data.type === "thinking_end") {
            thinkingRef.current = "";
            setThinkingText("");
          } else if (data.type === "stream_end") {
            const finalText = data.content || streamingRef.current || "";
            const completedTools = streamingToolsRef.current.filter(t => t.status === "done");
            streamingRef.current = "";
            thinkingRef.current = "";
            streamingToolsRef.current = [];
            setStreamingText("");
            setThinkingText("");
            setMessages((prev) => [...prev, {
              role: "assistant",
              text: finalText,
              time: Date.now(),
              animate: true,
              tools: completedTools.length > 0 ? completedTools : undefined,
            }]);
            setTyping(false);
            if (storage.get("tts_enabled", false)) speak(finalText);
          } else if (data.type === "tool_start") {
            streamingToolsRef.current = [...streamingToolsRef.current, {
              name: data.tool,
              description: data.description,
              status: "running",
            }];
          } else if (data.type === "tool_end") {
            streamingToolsRef.current = streamingToolsRef.current.map(t =>
              t.name === data.tool && t.status === "running"
                ? { ...t, status: data.success ? "done" : "error" }
                : t
            );
          } else if (data.type === "photo_comparison") {
            setMessages((prev) => [...prev, { role: "comparison", data, time: Date.now() }]);
          } else if (data.type === "message" && data.role === "assistant") {
            setMessages((prev) => [...prev, { role: "assistant", text: data.content, time: Date.now() }]);
            setTyping(false);
          } else if (data.type === "error") {
            // Clean up any partial streaming content on error
            streamingRef.current = "";
            thinkingRef.current = "";
            setStreamingText("");
            setThinkingText("");
            // If the error indicates token expiry, auto-reconnect with fresh token
            const errMsg = (data.content || "").toLowerCase();
            if (errMsg.includes("token") && (errMsg.includes("expired") || errMsg.includes("invalid"))) {
              ws.close();
              reconnectAttemptsRef.current = 0;
              reconnectRef.current = setTimeout(connectWebSocket, 500);
              return;
            }
            setMessages((prev) => [...prev, { role: "assistant", text: `Sorry, something went wrong: ${data.message || data.content || "Unknown error"}`, time: Date.now() }]);
            setTyping(false);
          } else if (data.type === "cancelled") {
            streamingRef.current = "";
            thinkingRef.current = "";
            setStreamingText("");
            setThinkingText("");
            setTyping(false);
          } else if (data.type === "queued") {
            // Message queued while AI is still responding - keep typing indicator
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = (event) => {
        if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
        // Only reconnect if this is still the active socket
        if (wsRef.current !== ws) return;
        setConnected(false);
        wsRef.current = null;
        // Code 4401 or 4403 = token expired/unauthorized — reconnect immediately with fresh token
        if (event.code === 4401 || event.code === 4403) {
          reconnectAttemptsRef.current = 0;
          reconnectRef.current = setTimeout(connectWebSocket, 500);
          return;
        }
        // Exponential backoff: 3s, 4.5s, 6.75s, ... capped at 30s
        const delay = Math.min(3000 * Math.pow(1.5, reconnectAttemptsRef.current), 30000);
        reconnectAttemptsRef.current++;
        reconnectRef.current = setTimeout(connectWebSocket, delay);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      // Token fetch failed, retry with backoff
      const delay = Math.min(3000 * Math.pow(1.5, reconnectAttemptsRef.current), 30000);
      reconnectAttemptsRef.current++;
      reconnectRef.current = setTimeout(connectWebSocket, delay);
    }
  }, []);

  useEffect(() => {
    connectWebSocket();

    // Reconnect when app returns from background (iOS kills WebSocket on suspend)
    const handleVisibility = () => {
      if (document.visibilityState === "hidden") {
        // Save any in-progress streaming text before iOS kills the WebSocket
        if (streamingRef.current) {
          storage.set("coach_partial_stream", streamingRef.current);
        }
      } else if (document.visibilityState === "visible") {
        // Recover partial stream if WebSocket was killed mid-response
        const partial = storage.get("coach_partial_stream", null);
        if (partial && streamingRef.current === "") {
          setMessages((prev) => [...prev, {
            role: "assistant",
            text: partial + "\n\n[Response interrupted — please ask me to continue if needed]",
            time: Date.now(),
          }]);
          streamingRef.current = "";
          setStreamingText("");
          setTyping(false);
        }
        storage.set("coach_partial_stream", null);
        if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          if (reconnectRef.current) clearTimeout(reconnectRef.current);
          reconnectAttemptsRef.current = 0;
          connectWebSocket();
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibility);
      if (wsRef.current) wsRef.current.close();
      if (reconnectRef.current) clearTimeout(reconnectRef.current);
      if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
    };
  }, [connectWebSocket]);

  // Persist messages to localStorage
  useEffect(() => {
    if (messages.length > 1 || messages[0]?.text !== defaultMsg.text) {
      storage.set("coach_messages", messages.slice(-100));
    }
  }, [messages]);

  // TTS — speak a coach message
  const speak = (text, msgIndex) => {
    if (typeof speechSynthesis === "undefined") return;
    const ttsEnabled = storage.get("tts_enabled", false);
    if (!ttsEnabled && msgIndex !== undefined) return;
    speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    const voiceId = storage.get("coach_voice", "default");
    if (voiceId !== "default") {
      const voices = speechSynthesis.getVoices();
      // Map our voice IDs to voice selection criteria
      const voicePrefs = {
        rachel: { gender: "female", keywords: ["samantha", "karen", "victoria", "zira", "female"] },
        adam: { gender: "male", keywords: ["daniel", "alex", "david", "male"] },
        bella: { gender: "female", keywords: ["fiona", "moira", "tessa", "female"] },
        marcus: { gender: "male", keywords: ["tom", "james", "aaron", "male"] },
      };
      const pref = voicePrefs[voiceId];
      if (pref && voices.length > 0) {
        // Try to find a matching voice by keyword in name
        const found = voices.find(v => {
          const name = v.name.toLowerCase();
          return pref.keywords.some(k => name.includes(k)) && v.lang.startsWith("en");
        }) || voices.find(v => v.lang.startsWith("en") && v.name.toLowerCase() !== "google us english");
        if (found) u.voice = found;
      } else {
        // Direct name match fallback
        const found = voices.find((v) => v.name === voiceId);
        if (found) u.voice = found;
      }
    }
    u.rate = 0.95;
    u.pitch = 1.0;
    setSpeakingId(msgIndex ?? -1);
    u.onend = () => setSpeakingId(null);
    u.onerror = () => setSpeakingId(null);
    speechSynthesis.speak(u);
  };

  const stopSpeaking = () => {
    if (typeof speechSynthesis !== "undefined") speechSynthesis.cancel();
    setSpeakingId(null);
  };

  // Voice input — speech recognition
  const hasSpeechRecognition = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  const startListening = () => {
    if (!hasSpeechRecognition || listening) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SR();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = "en-US";
    recognition.onresult = (event) => {
      const transcript = event.results[0]?.[0]?.transcript?.trim();
      if (transcript) setInput(transcript);
      setListening(false);
    };
    recognition.onerror = () => setListening(false);
    recognition.onend = () => setListening(false);
    recognitionRef.current = recognition;
    recognition.start();
    setListening(true);
  };

  const stopListening = () => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setListening(false);
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        // Only auto-scroll if user is near the bottom (within 150px)
        const el = scrollRef.current;
        const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 150;
        if (isNearBottom || typing) {
          el.scrollTop = el.scrollHeight;
        }
      }
    });
  }, [messages, typing, streamingText, thinkingText]);

  // Scroll to bottom when tab becomes visible (persistent tabs use display:none)
  useEffect(() => {
    if ((visible || isOverlay) && messages.length > 0) {
      setTimeout(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }, 50);
    }
  }, [visible]);

  const MAX_FILES = 5;
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const toAdd = files.slice(0, MAX_FILES - pendingFiles.length).filter((file) => {
      if (file.size > MAX_FILE_SIZE) {
        setMessages((prev) => [...prev, { role: "system", text: `File "${file.name}" is too large (max 10MB).`, time: Date.now() }]);
        return false;
      }
      return true;
    });
    toAdd.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const dataUrl = ev.target.result;
        const isImage = file.type.startsWith("image/");
        setPendingFiles((prev) => prev.length < MAX_FILES ? [...prev, {
          preview: isImage ? dataUrl : null,
          name: file.name,
          type: file.type,
          dataUrl,
        }] : prev);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const cancelResponse = () => {
    // Stop the AI response but preserve what was said
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "cancel" }));
    }
    const partialText = streamingRef.current;
    if (partialText) {
      setMessages((prev) => [...prev, {
        role: "assistant",
        text: partialText,
        time: Date.now(),
        tools: streamingToolsRef.current.filter(t => t.status === "done"),
      }]);
    }
    streamingRef.current = "";
    thinkingRef.current = "";
    streamingToolsRef.current = [];
    setStreamingText("");
    setThinkingText("");
    setTyping(false);
  };

  const send = () => {
    if (typing) return;
    const hasFiles = pendingFiles.length > 0;
    if (!input.trim() && !hasFiles) return;
    // If AI is mid-stream, commit what we have so far as a message
    if (typing && streamingRef.current) {
      setMessages((prev) => [...prev, { role: "assistant", text: streamingRef.current, time: Date.now() }]);
    }
    const userMsg = input.trim();
    const attachments = [...pendingFiles];
    const firstImage = attachments.find((f) => f.type?.startsWith("image/"));
    setMessages((prev) => [...prev, {
      role: "user",
      text: userMsg || (hasFiles ? (attachments.length > 1 ? `${attachments.length} files attached` : (firstImage ? "Photo attached" : attachments[0].name)) : ""),
      time: Date.now(),
      photo: firstImage?.dataUrl || null,
      fileName: !firstImage && attachments[0] ? attachments[0].name : null,
      fileCount: attachments.length > 1 ? attachments.length : undefined,
    }]);
    setInput("");
    setPendingFiles([]);
    setTyping(true);
    streamingRef.current = "";
    thinkingRef.current = "";
    setStreamingText("");
    setThinkingText("");

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const msg = { type: "message", content: userMsg || "" };
      if (attachments.length === 1) {
        const a = attachments[0];
        msg.file = { data: a.dataUrl.split(",")[1], mimeType: a.type, fileName: a.name };
      } else if (attachments.length > 1) {
        msg.files = attachments.map((a) => ({
          data: a.dataUrl.split(",")[1], mimeType: a.type, fileName: a.name,
        }));
      }
      wsRef.current.send(JSON.stringify(msg));
    } else {
      setTimeout(() => {
        setMessages((prev) => [...prev, { role: "assistant", text: "I'm having trouble connecting. Please check your connection and try again.", time: Date.now() }]);
        setTyping(false);
      }, 500);
    }
  };

  const quickPrompts = isWorkout
    ? ["Form check tips", "Adjust my rest time", "How's my volume today?", "Push me harder"]
    : ["What should I focus on today?", "Break down my nutrition", "How's my recovery looking?", "What needs work this week?"];

  const sendQuick = (prompt) => {
    if (typing) return;
    setShowPrompts(false);
    setMessages((prev) => [...prev, { role: "user", text: prompt, time: Date.now() }]);
    setTyping(true);
    streamingRef.current = "";
    thinkingRef.current = "";
    setStreamingText("");
    setThinkingText("");

    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "message", content: prompt }));
    } else {
      setTimeout(() => {
        setMessages((prev) => [...prev, { role: "assistant", text: "I'm having trouble connecting. Please check your connection and try again.", time: Date.now() }]);
        setTyping(false);
      }, 500);
    }
  };

  const formatTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  // During workout: slide up from bottom as a half-screen panel, NOT full takeover
  // Uses calc with CSS vars for safe area awareness and dvh fallback
  const wrapStyle = isOverlay ? (isWorkout ? {
    position: "fixed", left: 0, right: 0, bottom: 0,
    top: "25%", zIndex: 90,
    background: C.bg,
    display: "flex", flexDirection: "column",
    animation: "slideUp 0.3s cubic-bezier(0.16,1,0.3,1)",
    borderTop: `1px solid ${C.structBorderStrong}`,
    borderRadius: "20px 20px 0 0",
    boxShadow: `0 -8px 40px rgba(0,0,0,0.5), 0 0 20px ${C.accent010}`,
  } : {
    position: "fixed", inset: 0, zIndex: 100,
    background: C.bg,
    display: "flex", flexDirection: "column",
    animation: "slideUp 0.3s cubic-bezier(0.16,1,0.3,1)",
  }) : {
    display: "flex", flexDirection: "column",
    height: "calc(100vh - 180px - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))",
    marginTop: -4,
  };

  return (
    <div style={wrapStyle}>
      {/* Coach Header — only shown in overlay mode (tab mode uses the main app header) */}
      {isOverlay && (
        <div style={{
          display: "flex", alignItems: "center", gap: 'var(--sp-md)',
          padding: "calc(14px + env(safe-area-inset-top, 0px)) 20px 14px",
          borderBottom: `1px solid ${C.structBorder}`,
          background: C.headerBg,
          backdropFilter: "blur(20px)",
          flexShrink: 0,
        }}>
          <button onClick={onClose} style={{ background: "none", border: "none", color: C.accent, cursor: "pointer", padding: 4, display: "flex", alignItems: "center", minWidth: 36, minHeight: 44 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>
          </button>
          <ForgeLogo C={C} size="sm" />
          <div>
            <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 700, color: C.text1, fontFamily: "var(--d)" }}>Forge Coach</div>
            <div style={{ fontSize: 'var(--fs-2xs)', color: C.accent, fontFamily: "var(--m)", letterSpacing: ".1em" }}>
              {isWorkout ? "LIVE SESSION INTEL" : "ALWAYS ON · ALWAYS LEARNING"}
            </div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 'var(--gap-sm)' }}>
            <div style={{ width: 6, height: 6, borderRadius: 3, background: connected ? C.secondary : (C.warn || '#ff6b6b'), boxShadow: `0 0 8px ${connected ? C.secondary030 : 'rgba(255,107,107,0.3)'}`, animation: "pulse 3s ease-in-out infinite" }} />
            <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".08em" }}>{connected ? "ONLINE" : "CONNECTING"}</div>
          </div>
        </div>
      )}

      {/* Inline connection status for tab mode */}
      {!isOverlay && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 0 12px", borderBottom: `1px solid ${C.structBorder}`, flexShrink: 0 }}>
          <div style={{ width: 6, height: 6, borderRadius: 3, background: connected ? C.secondary : (C.warn || '#ff6b6b'), boxShadow: `0 0 8px ${connected ? C.secondary030 : 'rgba(255,107,107,0.3)'}`, animation: "pulse 3s ease-in-out infinite" }} />
          <div style={{ fontSize: 'var(--fs-2xs)', color: C.text4, fontFamily: "var(--m)", letterSpacing: ".08em" }}>{connected ? "ONLINE" : "CONNECTING"}</div>
        </div>
      )}

      {/* Messages */}
      <div ref={scrollRef} style={{ flex: 1, overflowY: "auto", WebkitOverflowScrolling: "touch", overscrollBehavior: "contain", display: "flex", flexDirection: "column", gap: 'var(--sp-md)', padding: isOverlay ? "16px 20px 8px" : "16px 0 8px" }}>
        {historyLoading && messages.length <= 1 && (
          <>
            {[0, 1, 2].map((k) => {
              const isUser = k % 2 === 1;
              return (
                <div key={`hskel-${k}`} style={{
                  display: "flex",
                  justifyContent: isUser ? "flex-end" : "flex-start",
                  gap: 'var(--sp-sm)',
                  animation: "pulse 1.5s ease-in-out infinite",
                  animationDelay: `${k * 0.12}s`,
                }}>
                  {!isUser && (
                    <div style={{
                      width: 28, height: 28, borderRadius: 8,
                      background: C.structBorderHover,
                      flexShrink: 0, marginTop: 2,
                    }} />
                  )}
                  <div style={{
                    maxWidth: "72%",
                    background: C.structBorderHover,
                    borderRadius: isUser ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                    padding: "14px 18px",
                    minWidth: isUser ? 140 : 200,
                    minHeight: 44,
                  }} />
                </div>
              );
            })}
            <div style={{
              textAlign: "center",
              fontSize: 'var(--fs-xs)',
              fontFamily: "var(--m)",
              color: C.text4,
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              padding: "6px 0",
            }}>Loading chat history…</div>
          </>
        )}
        {messages.map((m, i) => {
          const isHistoryAnim = historyAnimCount > 0 && i < historyAnimCount;
          const isNewReply = m.animate && i === messages.length - 1;
          const isLastAnim = !isHistoryAnim && !isNewReply && i === messages.length - 1;
          const anim = isHistoryAnim
            ? `fadeIn 0.35s ease ${i * 40}ms both`
            : isNewReply ? "replyIn 0.3s ease both"
            : isLastAnim ? "fadeIn 0.3s ease" : "none";
          return m.role === "comparison" ? (
            <div key={i} style={{ maxWidth: "90%", animation: anim }}>
              <PhotoComparisonCard data={m.data} C={C} />
            </div>
          ) :
          <div key={i} style={{ display: "flex", gap: 'var(--sp-sm)', flexDirection: m.role === "assistant" ? "row" : "row-reverse", animation: anim }}>
            {m.role === "assistant" && (
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: C.gradient, backgroundSize: "300% 100%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 'var(--fs-sm)', fontWeight: 900, color: C.btnText, fontFamily: "var(--d)",
                flexShrink: 0, marginTop: 2,
              }}>
                F
              </div>
            )}
            <div style={{
              background: m.role === "assistant"
                ? C.cardGradient
                : C.accent008,
              border: `1px solid ${m.role === "assistant" ? C.structBorderHover : C.accent015}`,
              borderRadius: m.role === "assistant" ? "4px 16px 16px 16px" : "16px 4px 16px 16px",
              padding: "12px 16px",
              maxWidth: "82%",
              boxShadow: m.role === "assistant" ? C.cardShadow : "none",
            }}>
              {m.photo && (
                <div style={{ marginBottom: 8, borderRadius: 10, overflow: "hidden" }}>
                  <img src={m.photo} alt="Shared photo" style={{ width: "100%", maxHeight: 180, objectFit: "cover", display: "block", borderRadius: 10 }} />
                </div>
              )}
              {m.fileName && !m.photo && (
                <div style={{ marginBottom: 8, display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", borderRadius: 10, background: C.accent010, border: `1px solid ${C.accent030}` }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
                  </svg>
                  <span style={{ fontSize: "var(--fs-sm)", color: C.text3 }}>{m.fileName}</span>
                </div>
              )}
              {m.text && <div style={{ fontSize: 'var(--fs-md)', color: m.role === "assistant" ? C.text2 : C.text1, lineHeight: 1.75, whiteSpace: "pre-line" }}>{m.role === "assistant" ? renderMarkdown(m.text) : m.text}</div>}
              <div style={{ fontSize: 'var(--fs-2xs)', color: C.text5, fontFamily: "var(--m)", marginTop: 6, display: "flex", alignItems: "center", justifyContent: m.role === "assistant" ? "space-between" : "flex-end" }}>
                <span>{formatTime(m.time)}</span>
                {m.role === "assistant" && typeof speechSynthesis !== "undefined" && (
                  <button onClick={(e) => { e.stopPropagation(); speakingId === i ? stopSpeaking() : speak(m.text, i); }} style={{
                    background: "none", border: "none", cursor: "pointer", padding: 2,
                    color: speakingId === i ? C.accent : C.text5, transition: "color 0.2s",
                    display: "flex", alignItems: "center",
                  }}>
                    {speakingId === i ? (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>
                    ) : (
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" /><path d="M15.54 8.46a5 5 0 010 7.07" /><path d="M19.07 4.93a10 10 0 010 14.14" />
                      </svg>
                    )}
                  </button>
                )}
              </div>
              {m.role === "assistant" && m.tools && m.tools.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                  {m.tools.map((tool, ti) => (
                    <span key={ti} style={{
                      display: "inline-flex", alignItems: "center", gap: 3,
                      fontSize: "var(--fs-2xs)", fontFamily: "var(--m)",
                      padding: "2px 6px", borderRadius: 4,
                      background: C.accent008, color: C.text4,
                    }}>
                      <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                      {TOOL_LABELS[tool.name] || tool.description || tool.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
        })}
        {/* Streaming message */}
        {typing && streamingText && (
          <div style={{ display: "flex", gap: 'var(--sp-sm)', animation: "fadeIn 0.2s ease" }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: C.gradient, backgroundSize: "300% 100%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 'var(--fs-sm)', fontWeight: 900, color: C.btnText, fontFamily: "var(--d)",
              flexShrink: 0, marginTop: 2,
            }}>F</div>
            <div style={{
              background: C.cardGradient,
              border: `1px solid ${C.structBorderHover}`,
              borderRadius: "4px 16px 16px 16px",
              padding: "12px 16px",
              maxWidth: "82%",
              boxShadow: C.cardShadow,
            }}>
              <div style={{ fontSize: 'var(--fs-md)', color: C.text2, lineHeight: 1.75, whiteSpace: "pre-line" }}>{renderMarkdown(streamingText)}</div>
            </div>
          </div>
        )}
        {/* Reasoning indicator — shows live thinking tokens before visible response starts */}
        {typing && !streamingText && thinkingText && (
          <div style={{ display: "flex", gap: 'var(--sp-sm)', animation: "fadeIn 0.2s ease" }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: C.gradient, backgroundSize: "300% 100%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 'var(--fs-sm)', fontWeight: 900, color: C.btnText, fontFamily: "var(--d)",
              flexShrink: 0, marginTop: 2,
            }}>F</div>
            <div style={{
              background: C.cardGradient,
              border: `1px dashed ${C.structBorderHover}`,
              borderRadius: "4px 16px 16px 16px",
              padding: "10px 14px",
              maxWidth: "82%",
              boxShadow: C.cardShadow,
              opacity: 0.75,
            }}>
              <div style={{
                fontSize: 'var(--fs-xs)',
                color: C.text3,
                fontFamily: "var(--m)",
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                marginBottom: 4,
                display: "flex", alignItems: "center", gap: 6,
              }}>
                <span style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: C.accent,
                  animation: `typingDot 1.4s ease-in-out infinite`,
                }} />
                Reasoning
              </div>
              <div style={{
                fontSize: 'var(--fs-sm)',
                color: C.text3,
                lineHeight: 1.5,
                fontStyle: "italic",
                whiteSpace: "pre-line",
                maxHeight: 160,
                overflow: "hidden",
                maskImage: "linear-gradient(to bottom, transparent 0, black 40px, black calc(100% - 40px), transparent 100%)",
                WebkitMaskImage: "linear-gradient(to bottom, transparent 0, black 40px, black calc(100% - 40px), transparent 100%)",
              }}>{thinkingText}</div>
            </div>
          </div>
        )}
        {/* Typing dots — shown before any thinking or text arrives */}
        {typing && !streamingText && !thinkingText && (
          <div style={{ display: "flex", gap: 'var(--sp-sm)', animation: "fadeIn 0.2s ease" }}>
            <div style={{
              width: 28, height: 28, borderRadius: 8,
              background: C.gradient, backgroundSize: "300% 100%",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 'var(--fs-sm)', fontWeight: 900, color: C.btnText, fontFamily: "var(--d)",
              flexShrink: 0,
            }}>F</div>
            <div style={{
              background: C.cardGradient,
              border: `1px solid ${C.structBorderHover}`,
              borderRadius: "4px 16px 16px 16px",
              padding: "14px 20px",
              display: "flex", gap: 5, alignItems: "center",
              boxShadow: C.cardShadow,
            }}>
              {[0, 1, 2].map((i) => (
                <div key={i} style={{
                  width: 7, height: 7, borderRadius: "50%",
                  background: C.accent,
                  animation: `typingDot 1.4s ease-in-out infinite ${i * 0.2}s`,
                }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Quick Prompts — togglable, always accessible */}
      {showPrompts && (
        <div style={{ padding: isOverlay ? "8px 20px" : "8px 0", display: "flex", gap: 'var(--gap-sm)', overflowX: "auto", flexShrink: 0 }}>
          {quickPrompts.map((prompt) => (
            <button key={prompt} onClick={() => sendQuick(prompt)} style={{
              padding: "8px 14px",
              background: C.structGlass,
              border: `1px solid ${C.structBorderHover}`,
              borderRadius: 20,
              color: C.text3,
              fontSize: 'var(--fs-sm)',
              fontFamily: "var(--m)",
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "all 0.2s",
              letterSpacing: ".04em",
            }}>
              {prompt}
            </button>
          ))}
        </div>
      )}
      {!showPrompts && messages.length > 2 && (
        <div style={{ padding: isOverlay ? "4px 20px" : "4px 0", flexShrink: 0 }}>
          <button onClick={() => setShowPrompts(true)} style={{
            padding: "6px 12px", background: "none", border: `1px solid ${C.structBorderHover}`,
            borderRadius: 16, color: C.text4, fontSize: 'var(--fs-xs)', fontFamily: "var(--m)", cursor: "pointer",
            letterSpacing: ".06em",
          }}>
            SUGGESTIONS
          </button>
        </div>
      )}

      {/* File Previews */}
      {pendingFiles.length > 0 && (
        <div style={{ padding: isOverlay ? "8px 20px 0" : "8px 0 0", flexShrink: 0 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            {pendingFiles.map((pf, i) => (
              <div key={i} style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: 8, borderRadius: 10, overflow: "hidden", border: `1px solid ${C.accent030}`, padding: pf.preview ? 0 : "8px 28px 8px 10px" }}>
                {pf.preview ? (
                  <img src={pf.preview} alt="" style={{ height: 56, borderRadius: 10, display: "block", objectFit: "cover" }} />
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={C.accent} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" />
                    </svg>
                    <span style={{ fontSize: "var(--fs-xs)", color: C.text3, maxWidth: 100, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{pf.name}</span>
                  </>
                )}
                <button onClick={() => setPendingFiles((prev) => prev.filter((_, j) => j !== i))} style={{
                  position: "absolute", top: 2, right: 2, width: 18, height: 18, borderRadius: 9,
                  background: "rgba(0,0,0,0.7)", border: "none", color: "#fff", fontSize: 'var(--fs-sm)',
                  cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                </button>
              </div>
            ))}
            {pendingFiles.length > 1 && (
              <button onClick={() => setPendingFiles([])} style={{
                background: "none", border: "none", color: C.text4, fontSize: "var(--fs-xs)",
                fontFamily: "var(--m)", cursor: "pointer", letterSpacing: ".06em",
              }}>CLEAR ALL</button>
            )}
          </div>
        </div>
      )}

      {/* Input */}
      <input ref={photoRef} type="file" accept="image/*" capture="environment" onChange={handleFileSelect} style={{ display: "none" }} />
      <input ref={galleryRef} type="file" accept="image/*,application/pdf" multiple onChange={handleFileSelect} style={{ display: "none" }} />
      <div style={{ padding: isOverlay ? "12px 20px max(12px, env(safe-area-inset-bottom))" : "12px 0 12px", borderTop: `1px solid ${C.structBorderHover}`, display: "flex", gap: 6, flexShrink: 0 }}>
        <button onClick={() => photoRef.current?.click()} title="Take photo" style={{
          background: C.structGlass, border: `1.5px solid ${C.structBorderHover}`,
          borderRadius: 12, width: 36, height: 44, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: pendingFiles.length ? C.accent : C.text4, transition: "color 0.2s", flexShrink: 0,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" /><circle cx="12" cy="13" r="4" />
          </svg>
        </button>
        <button onClick={() => galleryRef.current?.click()} title="Upload files" style={{
          background: C.structGlass, border: `1.5px solid ${C.structBorderHover}`,
          borderRadius: 12, width: 36, height: 44, cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: C.text4, transition: "color 0.2s", flexShrink: 0,
          marginRight: 2,
        }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <polyline points="21 15 16 10 5 21" />
          </svg>
        </button>
        {hasSpeechRecognition && (
          <button onClick={listening ? stopListening : startListening} style={{
            background: listening ? C.accent015 : C.structGlass,
            border: `1.5px solid ${listening ? C.accent : C.structBorderHover}`,
            borderRadius: 12, width: 36, height: 44, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            color: listening ? C.accent : C.text4, transition: "all 0.2s", flexShrink: 0,
            animation: listening ? "pulse 1.5s ease-in-out infinite" : "none",
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z" />
              <path d="M19 10v2a7 7 0 01-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </button>
        )}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder={isWorkout ? "Ask your coach anything..." : "Talk to your coach..."}
          style={{
            flex: 1, padding: "12px 16px",
            background: C.structGlass,
            border: `1.5px solid ${C.structBorderHover}`,
            borderRadius: 12,
            color: C.text1,
            fontSize: 'var(--fs-md)',
            fontFamily: "var(--b)",
            outline: "none",
            transition: "border-color 0.2s",
          }}
        />
        <button
          onClick={typing ? cancelResponse : send}
          disabled={!typing && !input.trim() && !pendingFiles.length}
          style={{
            background: (!typing && !input.trim() && !pendingFiles.length) ? C.accent020 : C.gradientBtn,
            backgroundSize: "300% 100%",
            border: "none",
            borderRadius: 12,
            color: C.btnText,
            fontSize: 'var(--fs-xl)',
            fontWeight: 700,
            width: 48,
            height: 48,
            cursor: (!typing && !input.trim() && !pendingFiles.length) ? "default" : "pointer",
            animation: (!typing && (input.trim() || pendingFiles.length)) ? "shimmerSlow 8s ease-in-out infinite" : "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "all 0.2s",
          }}
        >
          {typing ? (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <rect x="4" y="4" width="16" height="16" rx="2" />
              </svg>
            ) : (
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            )}
        </button>
      </div>
    </div>
  );
}
